import React, { PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';
import {
    MappedComponentProperties,
} from '@adobe/aem-react-editable-components';
import { useWidthContext } from '../../providers/Width';
import { MultiOfferContent, IOfferResult } from '../../types/responses/MultiOffer';
import { TPrequalResponse } from '../../types/responses/PrequalResponse';
import SessionStorage from '../../utils/SessionStorage';
import { multiOfferMockedData } from './MultiOfferBlockMockData';
import { OfferColumn } from './OfferColumn/OfferColumn';
import { SliderArrowGroup } from './SliderArrowGroup/SliderArrowGroup';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import './MultiOfferBlock.css';
import { equalizeHeights, getParameterByName } from '../../utils/cob-utils';
import axios from 'axios';
import { IOfferModuleCF } from '../../types/shared/OfferModule';
import { acquisitionApiRequest } from '../../utils/acquisition-api/AcquisitionApi';
import { clearSessionStorageVariablesForAcquisitionFlow } from '../../utils/acquisition-utils';
import { AcquisitionsLoader } from '../AcquisitionsLoader/AcquisitionsLoader';
import { CarouselSlider, Container } from '../../importCL';

interface IMultiOfferBlockProps extends MappedComponentProperties {
    header?: string;
    rateTitle?: string;
    rewardTitle?: string;
    applyNowCTAText?: string;
    scenarioOneMessage?: string;
    scenarioTwoMessage?: string;
    scenarioThreeMessage?: string;
    fullAppUrl?: string;
    dataEntryUrl?: string;
    errorCode?: string;
    complexAnnualFeeContentFragmentPath?: string;
}

export interface ISlideValidState {
    show: boolean;
    evaluated: boolean;
}

export const MultiOfferBlockEditConfig = {
    emptyLabel: 'Multi Offer Block',
    isEmpty: (props: PropsWithChildren<IMultiOfferBlockProps>) => {
        return !props || !props.header;
    },
};

const MultiOfferBlock = (
    props: PropsWithChildren<IMultiOfferBlockProps>,
): JSX.Element => {
    const isAuthor = (window.location.host.indexOf('4502') > -1) || (window.location.host.indexOf('3000') > -1);
    const { width } = useWidthContext();
    const customArrowsRef = useRef<any>(null);
    const [offerBlockContent, setOfferBlockContent] = useState<Record<string, any>>([]);
    const [slideIndex, setSlideIndex] = useState<number>(0);
    const [offerBlockContentSlides, setOfferBlockContentSlides] = useState<Array<typeof OfferColumn>>([]);
    const [showSlides, setShowSlides] = useState<Array<ISlideValidState>>([]);
    const [isDisabledCTA, setIsDisabledCTA] = useState<boolean>(false);

    const showArrowGroup = useMemo(() => {
        return width < 767 && offerBlockContent.length === 3;
    }, [width, offerBlockContent]);

    const equalizeHeightCallback = () => {
        const selectors = [
            '.OfferColumn__flagWrapper',
            '.OfferColumn__cardTitle',
            '.OfferColumn__strikethroughRatesTitle',
            '.OfferColumn__ratesContainer',
            '.OfferColumn__strikethroughRewardsTitle',
            '.OfferColumn__rewardsContainer',
        ];

        equalizeHeights(selectors);
    };

    const getOfferBlockContent = async () => {
        if (isAuthor) {
            setOfferBlockContent(multiOfferMockedData);
            setShowSlides(Array.from(multiOfferMockedData, () => {
                return { evaluated: false, show: true };
            }));
            return;
        }

        const multiOfferContent: MultiOfferContent = SessionStorage.getObject('multiOfferContent');
        const prequalResponse: TPrequalResponse = await SessionStorage.tryGetCipher('prequalResponse', undefined);

        if (!prequalResponse) {
            console.error('Missing prequalResponse information...');
            return;
        }

        if (!multiOfferContent) {
            console.error('Missing multiOfferContent information...');
            return;
        }

        const applicationInformation = prequalResponse.ApplicationInformation ?? null;

        if (!applicationInformation) {
            return;
        }

        const offerContent: Record<string, any> = multiOfferContent.result.reduce((offerContent: IOfferResult[], offer: IOfferResult) => {
            const offerProgramCode = offer.programCode.toLowerCase();
            for (const appInfo of applicationInformation) {
                if (appInfo.ProgramRates.ProgramCode.toLowerCase() === offerProgramCode) {
                    offer.programRates = appInfo.ProgramRates;
                    offer.CardDesign = appInfo.CardDesign;
                    offerContent.push(offer);
                    break;
                }
            }

            return offerContent;
        }, []);

        setOfferBlockContent(offerContent);
        setShowSlides(Array.from(offerContent as Array<any>, () => {
            return { evaluated: false, show: true };
        }));
    };

    const handleSlideChangeCallback = (_currentIndex: number, nextIndex: number) => {
        setSlideIndex(nextIndex);
    };

    useEffect(() => {
        getOfferBlockContent().catch(console.error);
    }, []);

    useEffect(() => {
        if (offerBlockContent.length && showSlides.length) {
            const slides = offerBlockContent.map((offerItem: IOfferResult, i: number) => {
                if (showSlides[i].show) {
                    return (
                        <OfferColumn
                            key={i}
                            offerData={offerItem}
                            index={i + 1}
                            parentProps={props}
                            equalizeHeightCallback={equalizeHeightCallback}
                            showSlides={showSlides}
                            setShowSlides={setShowSlides}
                            isDisabledCTA={isDisabledCTA}
                            handleDisableCTA={() => {setIsDisabledCTA(true)}}
                        />
                    );
                }
            });

            if (!showSlides.filter(state => !state.evaluated).length) {
                const offersWithValidTerms = showSlides.filter(state => state.show === true).length;

                if (offersWithValidTerms === 0) {
                    window.location.assign(`/pre-qualification/data-entry/index?${window.location.search ? `${window.location.search.substring(1)}&` : ''}errorCode=noTncError`);
                } else if (offersWithValidTerms === 1) {
                    const validIndex = showSlides.findIndex(state => state.show === true);
                    const offerData = offerBlockContent[validIndex];
                    const programCode = offerData.programCode;
                    const partnerId = getParameterByName('C1BPartnerID') || '';
                    const params = `${programCode}${partnerId === '' ? '' : `&partnerId=${partnerId}`}`;

                    acquisitionApiRequest(
                        'send-accepted-programCode',
                        { body: { programCode: programCode }, method: 'POST' },
                    ).then((response) => {
                        if (response.status === 200) {
                            SessionStorage.remove('cardInfo');
                            axios.get(`/services/appoffermodule?programCode=${params}`)
                                .then(response => response.data as IOfferModuleCF)
                                .then(data => {
                                    SessionStorage.set('selectedProgramCode', programCode);
                                    SessionStorage.setObject('offerContent', data ?? {});
                                    SessionStorage.setObject('cardInfo', offerData?.CardDesign?.Cards ?? {});
                                    window.location.assign(props.fullAppUrl + window.location.search);
                                })
                                .catch(() => {
                                    window.location.assign('/pre-qualification/data-entry/index?errorCode=noTncError');
                                });
                        } else if (response.status === 408) {
                            clearSessionStorageVariablesForAcquisitionFlow();
                            window.location.assign(`/pre-qualification/data-entry/index?${window.location.search ? 'window.location.search.substring(1)&' : ''}errorCode=noTncError`);
                        }
                    });

                    
                }
            }

            setOfferBlockContentSlides(slides);
        }
    }, [offerBlockContent, showSlides, isDisabledCTA]);

    return (
        <React.Fragment>
            {!!showSlides.filter(state => !state.evaluated).length && (<AcquisitionsLoader processingSteps={['Validating personal information']}></AcquisitionsLoader>)}
            <Container className={`MultiOfferBlock ${showSlides.filter(state => !state.evaluated).length || [0, 1].includes(showSlides.filter(state => state.show === true).length) ? 'hidden' : ''}`}>
                {props.header && (
                    <h2 className={`MultiOfferBlock__header ${offerBlockContent.length < 3 ? 'MultiOfferBlock__header--two' : ''}`}>
                        {transformHTMLParserLinks(props.header)}
                    </h2>
                )}
                {showArrowGroup && (
                    <SliderArrowGroup
                        ref={customArrowsRef}
                        slide={slideIndex}
                        count={offerBlockContent.length}
                        windowScreen={width}
                    />
                )}
                <CarouselSlider
                    className={`MultiOfferBlock__slider ${offerBlockContent.length < 3
                        ? 'MultiOfferBlock__slider--two'
                        : ''
                    }`}
                    hideDefaultArrows={true}
                    customArrows={{
                        onNextCallback: handleSlideChangeCallback,
                        onPreviousCallback: handleSlideChangeCallback,
                        nextArrowRef: customArrowsRef?.current?.nextArrowRef,
                        previousArrowRef: customArrowsRef?.current?.previousArrowRef,
                    }}
                    slidesToShow={width >= 768 ? 3 : 2}
                    slides={offerBlockContentSlides}
                />
            </Container>
        </React.Fragment>
    );
};

export default MultiOfferBlock;
